import firebase from '../config/FirebaseConfig'

// Deterministic doc id keyed by user + brand. Eliminates the read-before-write
// race where two concurrent first-pins each create a separate random-id doc.
const pinnedDocId = (userId, brandId) => `${userId}_${brandId}`
const pinnedDoc = (userId, brandId) =>
  firebase
    .firestore()
    .collection('pinned-hooks')
    .doc(pinnedDocId(userId, brandId))

// Legacy shape: docs written with a random id before the deterministic-id migration.
const legacyPinnedQuery = (userId, brandId) =>
  firebase
    .firestore()
    .collection('pinned-hooks')
    .where('userId', '==', userId)
    .where('brandId', '==', brandId)
    .limit(1)

// Pinned hooks used to be resolved server side. They're now read here and passed to the
// hooks endpoint as exact keys, so this prefers the deterministic doc and falls back to
// the legacy query-by-fields shape.
export async function getPinnedHookKeys ({ userId, brandId }) {
  if (!userId || !brandId) return []

  try {
    const primary = await pinnedDoc(userId, brandId).get()
    if (primary.exists) return primary.data()?.hookKeys ?? []

    const snapshot = await legacyPinnedQuery(userId, brandId).get()
    return snapshot.empty ? [] : snapshot.docs[0].data()?.hookKeys ?? []
  } catch (error) {
    console.error('Error fetching pinned hooks:', error)
    throw error
  }
}

export async function pinHook ({ userId, brandId, hookKey }) {
  try {
    // arrayUnion + merge is idempotent — safe under concurrent calls.
    await pinnedDoc(userId, brandId).set(
      {
        userId,
        brandId,
        hookKeys: firebase.firestore.FieldValue.arrayUnion(hookKey),
        updatedAt: new Date()
      },
      { merge: true }
    )
    return true
  } catch (error) {
    console.error('Error pinning hook:', error)
    throw error
  }
}

export async function unpinHook ({ userId, brandId, hookKey }) {
  try {
    await pinnedDoc(userId, brandId).set(
      {
        hookKeys: firebase.firestore.FieldValue.arrayRemove(hookKey),
        updatedAt: new Date()
      },
      { merge: true }
    )
    return true
  } catch (error) {
    console.error('Error unpinning hook:', error)
    throw error
  }
}

export async function clearHookPins ({ userId, brandId }) {
  try {
    await pinnedDoc(userId, brandId).set(
      {
        hookKeys: [],
        updatedAt: new Date()
      },
      { merge: true }
    )
    return true
  } catch (error) {
    console.error('Error clearing hook pins:', error)
    throw error
  }
}
